import { Spin } from 'antd'
import { createBrowserHistory } from 'history'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import React, { ReactElement, useEffect } from 'react'
import { Router } from 'react-router-dom'

import NavigationProvider from './NavigationContext'
import ApiProvider from './api/ApiContext'
import { useAuth } from './authentication/AuthContext'

const Unauthenticated = React.lazy(() => import('./authentication/AuthPage'))
const ClientApp = React.lazy(() => import('./ClientApp'))
const VictorApp = React.lazy(() => import('./VictorApp'))

const FullPageLoad = (): ReactElement => {
  return (
    <Spin
      spinning
      style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    />
  )
}

const Loading = (): ReactElement => {
  return <Spin spinning style={{ display: 'flex', justifyContent: 'center' }} />
}

export { FullPageLoad, Loading }

const BASENAME = '/payments'

const customHistory = createBrowserHistory({ basename: BASENAME })

export default function App(): ReactElement {
  const { activeUser } = useAuth()
  const ldClient = useLDClient()

  useEffect(() => {
    if (activeUser && ldClient) {
      ldClient.identify({
        key: activeUser?.uuid,
        firstName: activeUser?.first_name,
        lastName: activeUser?.last_name,
        email: activeUser?.email_address,
        custom: {
          clientUuid: activeUser?.organization_uuid as string,
          organizationUuid: activeUser?.organization_uuid as string,
          roles: Object.keys(activeUser?.roles) as string[],
        },
      })
    }
  }, [activeUser, ldClient])

  if (!activeUser) {
    return (
      <React.Suspense fallback={<FullPageLoad />}>
        <Unauthenticated />
      </React.Suspense>
    )
  }

  let UserApp
  switch (activeUser.group) {
    case 'admin':
      UserApp = VictorApp
      break
    case 'client':
      UserApp = ClientApp
      break
    default:
      console.error('Authenticated user is not assigned a group')
      UserApp = FullPageLoad
  }

  return (
    <React.Suspense fallback={<FullPageLoad />}>
      <ApiProvider>
        <Router history={customHistory}>
          <NavigationProvider>
            <UserApp />
          </NavigationProvider>
        </Router>
      </ApiProvider>
    </React.Suspense>
  )
}

export type HeaderText = {
  title: string | ReactElement
  message?: string | ReactElement
}

export type AppPageProps = {
  updateText: (text: HeaderText) => void
}
